/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, Text, VFlow } from 'bold-ui'
import { resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { ExemplosVerticalField, ExemplosVerticalFieldOptions } from '../../components/ExemplosVerticalField'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta11ExemplosEnum,
  ConsultaSeguimentoPergunta11Model,
} from './model-consultaSeguimentoPergunta11'
import {
  getPergunta11ResultadoText,
  hasPergunta11ExemplosFalhouAnySim,
  hasPergunta11ExemplosPassouAnySim,
  isPergunta11ExemplosAllFilled,
  isPergunta11ExemplosAllNao,
  isPergunta11ExemplosFalhouAllNao,
  isPergunta11ExemplosPassouAllNao,
} from './utils-consultaSeguimentoPergunta11'

interface ConsultaSeguimentoPergunta11FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta11Model>
}

export function ConsultaSeguimentoPergunta11Field(props: Readonly<ConsultaSeguimentoPergunta11FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta11Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta11Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isExemplosAllFilled = isPergunta11ExemplosAllFilled(value?.PERGUNTA_11_EXEMPLOS)
  const hasPassouAnySim = hasPergunta11ExemplosPassouAnySim(value?.PERGUNTA_11_EXEMPLOS)
  const hasFalhouAnySim = hasPergunta11ExemplosFalhouAnySim(value?.PERGUNTA_11_EXEMPLOS)
  const resultadoText = getPergunta11ResultadoText(value)
  const showPerguntaFrequencia = isExemplosAllFilled && hasPassouAnySim && hasFalhouAnySim

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_11}
          title={
            <ConsultaSeguimentoPerguntaHeaderTitle
              prefix='Quando você sorri para'
              sufix=', ele sorri de volta para você?'
            />
          }
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_11_HEADER} />}
        />
        {value.PERGUNTA_11_HEADER === SimNaoEnum.NAO && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              <ConsultaSeguimentoIndicatorHelper title='Quando você aponta para algo, o que o seu filho, em geral, faz?' />
            </VFlow>
            <VFlow vSpacing={0}>
              <ExemplosVerticalField
                name={name.PERGUNTA_11_EXEMPLOS}
                label='Responda todos os exemplos, conforme as respostas do genitor'
                titlePassou='Seu filho...'
                titleFalhou='Ele sempre...'
                options={PERGUNTA_11_EXEMPLOS_OPTIONS}
                required
              />
              {showPerguntaFrequencia && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoIndicatorStep
                    text={
                      <VFlow vSpacing={0}>
                        <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                        <Heading level={3} color='inherit'>
                          Pergunte sobre a frequência
                        </Heading>
                      </VFlow>
                    }
                    style={styles.indicatorStep}
                  />
                  <RadioGroupBoxField
                    name={name.PERGUNTA_11_FREQUENCIA}
                    label='Quais os que ele faz com mais frequência?'
                    options={[
                      {
                        label: 'Exemplos PASSOU',
                        value: PassouFalhouEnum.PASSOU,
                      },
                      {
                        label: 'Exemplos FALHOU',
                        value: PassouFalhouEnum.FALHOU,
                      },
                    ]}
                    required
                  />
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && <ConsultaSeguimentoIndicatorResultado resultado={value.resultado} text={resultadoText} />}
    </VFlow>
  )
}

const conditionToCalculate = (values: ConsultaSeguimentoPergunta11Model): boolean => {
  if (values?.PERGUNTA_11_HEADER === SimNaoEnum.SIM) {
    return true
  } else if (isPergunta11ExemplosAllFilled(values?.PERGUNTA_11_EXEMPLOS)) {
    const hasPassouAnySim = hasPergunta11ExemplosPassouAnySim(values?.PERGUNTA_11_EXEMPLOS)
    const hasFalhouAnySim = hasPergunta11ExemplosFalhouAnySim(values?.PERGUNTA_11_EXEMPLOS)

    if (hasPassouAnySim && hasFalhouAnySim) {
      return !isEmpty(values?.PERGUNTA_11_FREQUENCIA)
    } else {
      return true
    }
  }
  return false
}

const calculateResultado = (values: ConsultaSeguimentoPergunta11Model): PassouFalhouEnum => {
  if (values?.PERGUNTA_11_HEADER === SimNaoEnum.SIM) {
    return PassouFalhouEnum.PASSOU
  } else {
    const isPassouAllNao = isPergunta11ExemplosPassouAllNao(values?.PERGUNTA_11_EXEMPLOS)
    const isFalhouAllNao = isPergunta11ExemplosFalhouAllNao(values?.PERGUNTA_11_EXEMPLOS)
    const isExemplosAllNao = isPergunta11ExemplosAllNao(values?.PERGUNTA_11_EXEMPLOS)
    const hasPassouAnySim = hasPergunta11ExemplosPassouAnySim(values?.PERGUNTA_11_EXEMPLOS)
    const hasFalhouAnySim = hasPergunta11ExemplosFalhouAnySim(values?.PERGUNTA_11_EXEMPLOS)

    if (hasPassouAnySim && isFalhouAllNao) {
      return PassouFalhouEnum.PASSOU
    }

    if ((hasFalhouAnySim && isPassouAllNao) || isExemplosAllNao) {
      return PassouFalhouEnum.FALHOU
    }

    if (isPergunta11ExemplosAllFilled(values?.PERGUNTA_11_EXEMPLOS)) {
      return values?.PERGUNTA_11_FREQUENCIA
    }
  }
}

const PERGUNTA_11_EXEMPLOS_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta11ExemplosEnum> = {
  PASSOU: [
    {
      label: 'Sorri quando você sorri?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_SORRI,
    },
    {
      label: 'Sorri quando você entra no quarto?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_ENTRA_NO_QUARTO,
    },
    {
      label: 'Sorri quando você volta depois de ter estado fora de casa?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_QUANDO_VOCE_VOLTA,
    },
  ],
  FALHOU: [
    {
      label: 'Está sempre sorrindo?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.ESTA_SEMPRE_SORRINDO,
    },
    {
      label: 'Sorri para um brinquedo ou atividade favorita?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_PARA_BRINQUEDO_OU_ATIVIDADE,
    },
    {
      label: 'Sorri ao acaso ou sem nenhum motivo especial?',
      value: ConsultaSeguimentoPergunta11ExemplosEnum.SORRI_AO_ACASO_OU_SEM_MOTIVO,
    },
  ],
}

const styles = {
  indicatorStep: css`
    width: auto;
  `,
}
